import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { IBaseResponseError } from '../typescript/interfaces/common.interface';

const API_ERROR_TOAST_MESSAGE = "This Resturant can't get order right now";

const hasApiErrorFields = (data?: IBaseResponseError['data']) => {
  if (!data || typeof data !== 'object') return false;
  return 'error_type' in data && 'error_message' in data;
};

const resolveErrorMessage = (input?: IBaseResponseError['data']) => {
  if (hasApiErrorFields(input)) {
    return API_ERROR_TOAST_MESSAGE;
  }
  if (
    input &&
    typeof input === 'object' &&
    'message' in input &&
    typeof (input as IBaseResponseError).message === 'string'
  ) {
    return (input as IBaseResponseError).message as string;
  }
  if (typeof input === 'string') return input;
  return 'Something went wrong !';
};

export const US_Currency = (price: any) => {
  const USDollar = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });
  return USDollar.format(price);
};

export const US_Currency_Space = (price: any) => {
  const USDollar = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });
  return USDollar.format(price).replace(/^(\D+)/, '$ ');
};

export const toastSuccess = (message?: string, value?: any) => {
  toast.success(message || 'Success Notification !', {
    position: toast.POSITION.TOP_RIGHT,
    autoClose: 4000,
  });
};

export const toastError = (data?: IBaseResponseError | string) => {
  let message = '';
  if (typeof data !== 'string') message = resolveErrorMessage(data?.data);
  else message = data;
  toast.error(message, {
    position: toast.POSITION.TOP_RIGHT,
    autoClose: 4000,
  });
};

export const showApiErrorToastIfNeeded = (data?: IBaseResponseError) => {
  if (!hasApiErrorFields(data?.data)) return false;
  toastError(data);
  return true;
};

export function convertTo24HourFormat(dateTimeString: any) {
  const [datePart, timePart] = dateTimeString.split(' ');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hour, minute, second] = timePart.split(':').map(Number);
  const dateTime = new Date(year, month - 1, day, hour, minute, second);
  const formattedDateTime = dateTime.toLocaleString();
  return formattedDateTime;
}
export function convertTo12HourFormat(time24: string) {
  const [hours, minutes, seconds] = time24.split(':').map(Number);

  let period = 'AM';
  let hour12 = hours;

  if (hours === 0) {
    hour12 = 12;
  } else if (hours === 12) {
    period = 'PM';
  } else if (hours > 12) {
    hour12 = hours - 12;
    period = 'PM';
  }

  // Pad minutes with leading zero if needed
  const paddedMinutes = minutes.toString().padStart(2, '0');

  return `${hour12}:${paddedMinutes} ${period}`;
}

type Shift = {
  start_time: string; // "HH:mm:ss"
  end_time: string; // "HH:mm:ss"
};

export function getActiveShiftLabel(shifts: Shift[]): string | null {
  const now = dayjs();

  for (const shift of shifts) {
    const today = dayjs().format('YYYY-MM-DD');
    const start = dayjs(today + shift.start_time, 'HH:mm:ss');
    const end = dayjs(today + shift.end_time, 'HH:mm:ss');

    const isOvernight = end.isBefore(start);

    const isActive = isOvernight ? now.isAfter(start) || now.isBefore(end) : now.isAfter(start) && now.isBefore(end);

    if (isActive) {
      return `${start.format('hh:mm A')} - ${end.format('hh:mm A')}`;
    }
  }

  return null;
}

export const isDevServer = () =>
  (process.env.NODE_ENV === 'production' && !!window.location.host.includes('dev')) ||
  process.env.NODE_ENV === 'development';

// delete all cookies available

export const deleteAllCookies = () => {
  const cookies = document.cookie.split(';');

  cookies.forEach((cookie) => {
    const name = cookie.split('=')[0].trim();

    // Try different path levels
    const pathParts = location.pathname.split('/');

    for (let i = pathParts.length; i >= 0; i--) {
      const path = pathParts.slice(0, i).join('/') || '/';

      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path};`;
    }
  });
};

export const openGoogleMaps = (placeName: string) => {
  const encoded = encodeURIComponent(placeName);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);

  if (isIOS) {
    window.location.href = `comgooglemaps://?q=${encoded}`;
    setTimeout(() => {
      window.open(`https://www.google.com/maps/search/?api=1&query=${encoded}`, '_blank');
    }, 1000);
  } else if (isAndroid) {
    window.location.href = `geo:0,0?q=${encoded}`;
    setTimeout(() => {
      window.open(`https://www.google.com/maps/search/?api=1&query=${encoded}`, '_blank');
    }, 1000);
  } else {
    window.open(`https://www.google.com/maps/search/?api=1&query=${encoded}`, '_blank');
  }
};

// Offline Image Store

// Open DB helper
export function openImageDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request: IDBOpenDBRequest = indexedDB.open('image-store', 1);

    request.onupgradeneeded = (e: IDBVersionChangeEvent) => {
      const db: IDBDatabase = (e.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains('images')) {
        db.createObjectStore('images');
      }
    };

    request.onsuccess = (e: Event) => {
      resolve((e.target as IDBOpenDBRequest).result);
    };

    request.onerror = (e: Event) => {
      reject((e.target as IDBOpenDBRequest).error);
    };
  });
}

// Save image
export async function saveImage(key: string, imageUrl: string): Promise<void> {
  const db: IDBDatabase = await openImageDB();
  const response: Response = await fetch(imageUrl);
  const blob: Blob = await response.blob();

  return new Promise((resolve, reject) => {
    const tx: IDBTransaction = db.transaction('images', 'readwrite');
    const store: IDBObjectStore = tx.objectStore('images');
    const request: IDBRequest<IDBValidKey> = store.put(blob, key);

    request.onsuccess = () => resolve();
    request.onerror = (e: Event) => reject((e.target as IDBRequest).error);
  });
}

// Get image
export async function getImage(key: string): Promise<string | null> {
  const db: IDBDatabase = await openImageDB();

  return new Promise((resolve, reject) => {
    const tx: IDBTransaction = db.transaction('images', 'readonly');
    const store: IDBObjectStore = tx.objectStore('images');
    const request: IDBRequest<Blob> = store.get(key);

    request.onsuccess = (e: Event) => {
      const blob: Blob = (e.target as IDBRequest<Blob>).result;
      if (blob) {
        resolve(URL.createObjectURL(blob));
      } else {
        resolve(null);
      }
    };

    request.onerror = (e: Event) => reject((e.target as IDBRequest).error);
  });
}

// Delete image
export async function deleteImage(key: string): Promise<void> {
  const db: IDBDatabase = await openImageDB();

  return new Promise((resolve, reject) => {
    const tx: IDBTransaction = db.transaction('images', 'readwrite');
    const store: IDBObjectStore = tx.objectStore('images');
    const request: IDBRequest = store.delete(key);

    request.onsuccess = () => resolve();
    request.onerror = (e: Event) => reject((e.target as IDBRequest).error);
  });
}

// Save multiple images
export async function saveImages(entries: { key: string; imageUrl: string }[]): Promise<void> {
  const db: IDBDatabase = await openImageDB();

  const fetchedBlobs: { key: string; blob: Blob }[] = await Promise.all(
    entries.map(async ({ key, imageUrl }) => {
      const response: Response = await fetch(imageUrl);
      const blob: Blob = await response.blob();
      return { key, blob };
    }),
  );

  return new Promise((resolve, reject) => {
    const tx: IDBTransaction = db.transaction('images', 'readwrite');
    const store: IDBObjectStore = tx.objectStore('images');

    for (const { key, blob } of fetchedBlobs) {
      store.put(blob, key);
    }

    tx.oncomplete = () => resolve();
    tx.onerror = (e: Event) => reject((e.target as IDBTransaction).error);
  });
}

// Get multiple images
export async function getImages(keys: string[]): Promise<{ key: string; url: string | null }[]> {
  const db: IDBDatabase = await openImageDB();

  return new Promise((resolve, reject) => {
    const tx: IDBTransaction = db.transaction('images', 'readonly');
    const store: IDBObjectStore = tx.objectStore('images');

    const results: { key: string; url: string | null }[] = [];
    let completed: number = 0;

    for (const key of keys) {
      const request: IDBRequest<Blob> = store.get(key);

      request.onsuccess = (e: Event) => {
        const blob: Blob = (e.target as IDBRequest<Blob>).result;
        results.push({
          key,
          url: blob ? URL.createObjectURL(blob) : null,
        });

        completed++;
        if (completed === keys.length) resolve(results);
      };

      request.onerror = (e: Event) => reject((e.target as IDBRequest).error);
    }
  });
}

// Production checking

export const isProduction = () => !window.location.hostname.includes('dev');
